import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, ChevronLeft, FileImage, FileText, FolderOpen, Loader2, RefreshCw, Search, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { formatDateTime } from "../lib/tickets";
import { fileDownloadPath, fileOriginTarget, formatFileSize, recordsError } from "../lib/records";
import { ReceiptPreviewDialog } from "../components/finance/ReceiptPreview";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";

// One module's files: its own first page, « Charger plus » after. It tells the page when it has nothing, so that « Tous » can hide it.
function Section({ section, search, single, onPreview, onSettled, user }) {
  const [state, setState] = useState({ phase: "loading", items: [], cursor: null, more: false, error: "" });
  const [loadingMore, setLoadingMore] = useState(false);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setState((s) => ({ ...s, phase: "loading", error: "" }));
    try {
      const { data } = await api.get("/records/all-files", { params: { section: section.key, ...(search ? { q: search } : {}) } });
      if (mine !== seq.current) return;
      setState({ phase: "ready", items: data.items, cursor: data.cursor, more: data.more, error: "" });
      onSettled(section.key, data.items.length === 0 && !data.more ? "empty" : "full");
    } catch (e) {
      if (mine !== seq.current) return;
      setState((s) => ({ ...s, phase: "error", error: recordsError(e, "Impossible de charger ces fichiers.") }));
      onSettled(section.key, "full");
    }
  }, [section.key, search, onSettled]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get("/records/all-files", { params: { section: section.key, cursor: state.cursor, ...(search ? { q: search } : {}) } });
      setState((s) => ({ ...s, items: [...s.items, ...data.items.filter((i) => !s.items.some((p) => p.id === i.id))], cursor: data.cursor, more: data.more }));
    } catch (e) {
      setState((s) => ({ ...s, error: recordsError(e, "Impossible de charger la suite.") }));
    } finally {
      setLoadingMore(false);
    }
  };

  const empty = state.phase === "ready" && state.items.length === 0 && !state.more;
  if (empty && !single) return null;
  return (
    <section className="space-y-2" aria-labelledby={`files-${section.key}`} data-testid={`files-section-${section.key}`}>
      <div className="flex items-center gap-3">
        <h2 id={`files-${section.key}`} className="shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-500">{section.label}</h2>
        <div className="h-px flex-1 bg-slate-200" aria-hidden="true" />
        {state.phase === "ready" && state.items.length > 0 && (
          <span className="shrink-0 text-xs text-slate-500 tabular-nums">{state.items.length}{state.more ? "+" : ""} fichier{state.items.length > 1 || state.more ? "s" : ""}</span>
        )}
      </div>
      {state.phase === "loading" && <Card className="p-3 space-y-2"><Skeleton className="h-9 w-full" /><Skeleton className="h-9 w-3/4" /></Card>}
      {state.phase === "error" && (
        <Card className="p-4 flex flex-wrap items-center justify-between gap-2" role="alert">
          <p className="flex items-center gap-2 text-sm text-slate-700"><AlertTriangle size={16} className="text-amber-500" aria-hidden="true" />{state.error}</p>
          <Button variant="outline" size="sm" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {empty && <Card className="p-4 text-sm text-slate-500" data-testid="files-section-empty">{search ? "Aucun fichier de ce module ne correspond." : "Aucun fichier de ce module que vous pouvez ouvrir."}</Card>}
      {state.phase === "ready" && state.items.length > 0 && (
        <Card className="divide-y divide-slate-100">
          {state.items.map((item) => <FileRow key={item.id} item={item} onPreview={(i) => onPreview({ ...i, section: section.key })} user={user} />)}
        </Card>
      )}
      {state.phase === "ready" && state.more && (
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid={`files-more-${section.key}`}>
            {loadingMore && <Loader2 size={14} className="mr-1.5 animate-spin" />}{state.items.length ? "Charger plus" : "Continuer la recherche"}
          </Button>
          {state.error && <span className="text-sm text-red-600" role="alert">{state.error}</span>}
        </div>
      )}
    </section>
  );
}

function FileRow({ item, onPreview, user }) {
  const Icon = (item.mime || "").startsWith("image/") ? FileImage : FileText;
  const target = fileOriginTarget(item.origin, user);
  const origin = item.origin.detail ? `${item.origin.detail} · ${item.origin.label}` : item.origin.label;
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-0.5 px-3 py-2.5 lg:grid-cols-[auto_minmax(0,2fr)_minmax(0,2fr)_9rem_4.5rem] lg:items-center"
      data-testid="files-row">
      <Icon size={18} className="mt-0.5 text-slate-400 lg:mt-0" aria-hidden="true" />
      <button type="button" onClick={() => onPreview(item)} className="min-w-0 truncate text-left text-sm font-medium text-slate-900 hover:text-jamora hover:underline"
        title={item.name} data-testid="files-open">{item.name}</button>
      <div className="col-start-2 min-w-0 truncate text-xs text-slate-600 lg:col-start-auto lg:text-sm">
        {target ? <Link to={target.to} state={target.state} className="text-jamora hover:underline" title={origin} data-testid="files-origin">{origin}</Link>
          : <span title={origin}>{origin}</span>}
      </div>
      <div className="col-start-2 text-xs text-slate-500 lg:col-start-auto">
        {formatDateTime(item.at)}<span className="lg:hidden">{formatFileSize(item.size) ? ` · ${formatFileSize(item.size)}` : ""}</span>
      </div>
      <div className="hidden text-right text-xs text-slate-500 tabular-nums lg:block">{formatFileSize(item.size)}</div>
    </div>
  );
}

export default function RecordAllFiles() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const module = params.get("module") || "";
  const search = params.get("q") || "";
  const [searchInput, setSearchInput] = useState(search);
  const [sections, setSections] = useState({ phase: "loading", items: [], error: "" });
  const [settled, setSettled] = useState({});
  const [preview, setPreview] = useState(null);

  // From the address as it is NOW: the search's delayed write must never undo a module chip clicked meanwhile (the router's `prev` can
  // be the address of an earlier render).
  const setParam = useCallback((name, value) => {
    const next = new URLSearchParams(window.location.search);
    if (value) next.set(name, value); else next.delete(name);
    setParams(next, { replace: true });
  }, [setParams]);

  useEffect(() => {
    const t = setTimeout(() => { if (searchInput.trim() !== search) setParam("q", searchInput.trim()); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSections = useCallback(async () => {
    setSections({ phase: "loading", items: [], error: "" });
    try {
      const { data } = await api.get("/records/all-files/sections");
      setSections({ phase: "ready", items: data.sections, error: "" });
    } catch (e) {
      setSections({ phase: "error", items: [], error: recordsError(e, "Impossible de charger les modules.") });
    }
  }, []);
  useEffect(() => { loadSections(); }, [loadSections]);

  const shown = sections.items.filter((s) => !module || s.key === module);
  useEffect(() => { setSettled({}); }, [module, search]);
  const onSettled = useCallback((key, value) => setSettled((prev) => (prev[key] === value ? prev : { ...prev, [key]: value })), []);
  const allEmpty = !module && shown.length > 0 && shown.every((s) => settled[s.key] === "empty");
  const sectionLabel = (key) => sections.items.find((s) => s.key === key)?.label || "";

  return (
    <div className="space-y-4" data-testid="all-files-page">
      <Link to="/documents" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ChevronLeft size={14} />Documents et contrats</Link>
      <div className="min-w-0">
        <h1 className="font-display font-bold text-2xl text-slate-900">Tous les fichiers</h1>
        <p className="text-sm text-slate-500">Les fichiers des autres modules que vous pouvez déjà ouvrir, rangés par module. Rien n'est recopié : chacun reste dans son module et s'ouvre avec ses droits.</p>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="flex items-center gap-2 md:w-80 md:shrink-0">
          <div className="relative flex-1 min-w-0">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Nom du fichier, réunion, ticket…" className="pl-9 h-9"
              aria-label="Rechercher un fichier" data-testid="files-search" />
          </div>
          {search && <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={() => setSearchInput("")}><X size={14} className="mr-1" />Effacer</Button>}
        </div>
        {sections.items.length > 1 && (
          <div className="flex gap-1 overflow-x-auto pb-1 md:flex-wrap md:pb-0" role="tablist" aria-label="Modules">
            {[{ key: "", label: "Tous" }, ...sections.items].map((s) => (
              <button key={s.key || "all"} type="button" role="tab" aria-selected={module === s.key} onClick={() => setParam("module", s.key)}
                data-testid={`files-module-${s.key || "all"}`}
                className={`shrink-0 rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap ${module === s.key ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {sections.phase === "loading" && <div className="space-y-2"><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /></div>}
      {sections.phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{sections.error}</p>
          <Button variant="outline" onClick={loadSections}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {sections.phase === "ready" && (shown.length === 0 || allEmpty) && (
        <Card className="p-8 text-center space-y-2" data-testid="files-empty">
          <FolderOpen className="mx-auto text-slate-300" size={26} />
          <h3 className="font-medium text-slate-900">{search ? "Aucun fichier ne correspond" : "Aucun fichier à afficher"}</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            {search ? "Essayez un autre mot : le nom du fichier, ou ce à quoi il est rattaché (numéro de réunion, de ticket, de demande…)."
              : "Vous verrez ici les pièces jointes des réunions, tickets, achats, factures, missions et dépenses que vos droits vous permettent d'ouvrir."}
          </p>
        </Card>
      )}
      {sections.phase === "ready" && (
        <div className="space-y-6">
          {shown.map((s) => (
            <Section key={`${s.key}:${search}`} section={s} search={search} single={!!module} onPreview={setPreview} onSettled={onSettled} user={user} />
          ))}
        </div>
      )}

      <ReceiptPreviewDialog receipt={preview && { source: { key: `${preview.source}:${preview.id}`, path: fileDownloadPath(preview) }, name: preview.name }}
        onClose={() => setPreview(null)}
        description={preview ? `Fichier du module ${sectionLabel(preview.section)} : ${preview.origin.detail ? `${preview.origin.detail} · ` : ""}${preview.origin.label}. Chaque ouverture est journalisée.` : ""} />
    </div>
  );
}
